import React, { useState } from "react";
import {
  View,
  Text,
  Modal,
  TextInput,
  Pressable,
  StyleSheet,
  Platform,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import AdditionalFieldsModal, { Field } from "./AdditionalFieldsModal";

interface CancelConfirmationModalProps {
  visible: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

const CancelConfirmationModal: React.FC<CancelConfirmationModalProps> = ({
  visible,
  onKeepEditing,
  onDiscard,
}) => (
  <Modal visible={visible} transparent animationType="fade" onRequestClose={onKeepEditing}>
    <View style={styles.confirmationOverlay}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onKeepEditing} />
      <View style={styles.confirmationModal}>
        <View style={styles.confirmationHeader}>
          <View style={styles.confirmationHeading}>
            <View style={styles.warningIcon}>
              <MaterialIcons name="warning" size={22} color="#E87500" />
            </View>
            <Text style={styles.confirmationTitle}>Confirmation</Text>
          </View>
          <Pressable onPress={onKeepEditing} style={styles.close}>
            <MaterialIcons name="close" size={28} color="#202020" />
          </Pressable>
        </View>
        <View style={styles.confirmationBody}>
          <Text style={styles.confirmationMessage}>
            Are you sure you want to cancel? All progress will be lost.
          </Text>
        </View>
        <View style={styles.confirmationFooter}>
          <Pressable style={styles.confirmationNoButton} onPress={onKeepEditing}>
            <Text style={styles.backText}>No</Text>
          </Pressable>
          <Pressable style={styles.confirmationYesButton} onPress={onDiscard}>
            <Text style={styles.nextText}>Yes</Text>
          </Pressable>
        </View>
      </View>
    </View>
  </Modal>
);

type AddTertiaryModalProps = {
  visible: boolean;
  onClose: () => void;
  onSave: (tertiaryName: string, fields?: any[]) => void;
  existingNames?: string[];
  category: string;
  subcategory: string;
  onOpenFieldsModal?: () => void;
};

const AddTertiaryModal: React.FC<AddTertiaryModalProps> = ({
  visible,
  onClose,
  onSave,
  existingNames = [],
  category = "—",
  subcategory = "—",
  onOpenFieldsModal,
}) => {
  const [tertiaryName, setTertiaryName] = useState("");
  const [fields, setFields] = useState<Field[]>([]);
  const [fieldsVisible, setFieldsVisible] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const isDuplicate = Boolean(tertiaryName.trim()) && existingNames.some(
    (name) => name.trim().toLowerCase() === tertiaryName.trim().toLowerCase()
  );

  const handleSave = () => {
    if (tertiaryName.trim()) {
      onSave(tertiaryName, fields.length ? fields : undefined);
      setTertiaryName("");
      setFields([]);
    }
  };

  const handleClose = () => {
    setTertiaryName("");
    setFields([]);
    setFieldsVisible(false);
    setShowDiscardConfirm(false);
    onClose();
  };

  const requestClose = () => setShowDiscardConfirm(true);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={requestClose}
    >
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={requestClose} />
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>Add Tertiary Category</Text>
            <Pressable onPress={requestClose} style={styles.closeButton}>
              <MaterialIcons name="close" size={24} color="#034895" />
            </Pressable>
          </View>

          <View style={styles.body}>
            <View style={styles.field}>
              <Text style={styles.label}>Category</Text>
              <Text style={styles.value}>{category}</Text>
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>Subcategory</Text>
              <Text style={styles.value}>{subcategory}</Text>
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>
                Tertiary Category Name <Text style={styles.required}>*</Text>
              </Text>
              <TextInput
                style={[styles.input, isDuplicate && styles.inputError]}
                placeholder="e.g. Route Download"
                value={tertiaryName}
                onChangeText={setTertiaryName}
                maxLength={80}
                placeholderTextColor="#999"
              />
              {isDuplicate ? <Text style={styles.errorText}>Duplicate Tertiary</Text> : null}
            </View>

            <View style={styles.fieldsCard}>
              <View style={styles.fieldsRow}>
                  <View style={styles.fieldsInfo}>
                    <Text style={styles.fieldLabel}>Additional Fields</Text>
                    <Text style={styles.fieldHint}>
                      Optional. Shown on the Submit Ticket form for this
                      tertiary category.
                    </Text>
                  </View>
                  <Pressable
                    style={styles.addFieldsButton}
                    onPress={() => setFieldsVisible(true)}
                  >
                    <MaterialIcons name="add" size={16} color="#034895" />
                    <Text style={styles.addFieldsButtonText}>{fields.length ? `Edit Fields (${fields.length})` : "Add Fields"}</Text>
                  </Pressable>
                </View>
            </View>
              </View>

          <AdditionalFieldsModal
            visible={fieldsVisible}
            onClose={() => setFieldsVisible(false)}
            onSave={(savedFields) => { setFields(savedFields); setFieldsVisible(false); }}
            initialFields={fields}
            subtitle="Tertiary category"
          />

          <View style={styles.footer}>
            <Pressable
              style={[styles.button, styles.cancelButton]}
              onPress={requestClose}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </Pressable>
            <Pressable
              style={[
                styles.button,
                styles.saveButton,
                (!tertiaryName.trim() || isDuplicate) && styles.saveButtonDisabled,
              ]}
              onPress={handleSave}
              disabled={!tertiaryName.trim() || isDuplicate}
            >
              <Text style={styles.saveButtonText}>Add Tertiary</Text>
            </Pressable>
          </View>
          <CancelConfirmationModal
            visible={showDiscardConfirm}
            onKeepEditing={() => setShowDiscardConfirm(false)}
            onDiscard={handleClose}
          />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(20, 30, 50, 0.45)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: 12,
    minWidth: 480,
    maxWidth: 640,
    ...Platform.select({
      web: {
        maxHeight: "85vh",
        boxShadow: "0 8px 24px rgba(20, 30, 50, 0.16)",
      } as any,
      default: {
        shadowColor: "#141E32",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.16,
        shadowRadius: 24,
        elevation: 8,
      },
    }),
  },
  confirmationOverlay: {
    flex: 1,
    backgroundColor: "rgba(20, 30, 50, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  confirmationModal: {
    backgroundColor: "#fff",
    borderRadius: 8,
    width: "min(360px, 92%)",
    ...Platform.select({
      web: { boxShadow: "0 8px 24px rgba(20, 30, 50, 0.2)" } as any,
      default: { elevation: 8 },
    }),
  },
  confirmationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#E3E5E8",
  },
  confirmationHeading: { flexDirection: "row", alignItems: "center", gap: 10 },
  warningIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#FFE8D2",
    alignItems: "center",
    justifyContent: "center",
  },
  confirmationTitle: { fontSize: 18, fontWeight: "700", color: "#162235" },
  close: { padding: 4 },
  confirmationBody: { paddingHorizontal: 16, paddingVertical: 18 },
  confirmationMessage: { fontSize: 14, lineHeight: 20, color: "#424B59" },
  confirmationFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#E3E5E8",
  },
  confirmationNoButton: {
    minWidth: 90,
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderWidth: 2,
    borderColor: "#D0D4D9",
    borderRadius: 100,
  },
  confirmationYesButton: {
    minWidth: 90,
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 100,
    backgroundColor: "#075DB5",
  },
  backText: { color: "#034895", fontWeight: "700" },
  nextText: { color: "#fff", fontWeight: "700" },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#2D2D2D",
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  body: {
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  field: {
    marginBottom: 16,
  },
  fieldsCard: {
    marginBottom: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#F8F8F8",
    borderWidth: 1,
    borderColor: "#E4E4E4",
    borderRadius: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    color: "#585858",
    marginBottom: 6,
  },
  value: {
    fontSize: 14,
    fontWeight: "700",
    color: "#333",
  },
  required: {
    color: "#d32f2f",
  },
  input: {
    width: "100%",
    height: 40,
    borderWidth: 1,
    borderColor: "#D9D9D9",
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: "#2D2D2D",
  },
  inputError: {
    borderColor: "#D32F2F",
  },
  errorText: {
    color: "#D32F2F",
    fontSize: 12,
    marginTop: 6,
  },
  fieldsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  fieldsInfo: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    marginBottom: 4,
  },
  fieldHint: {
    fontSize: 12,
    color: "#666",
  },
  addFieldsButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 7,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#034895",
    borderRadius: 100,
    minHeight: 34,
  },
  addFieldsButtonText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#034895",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: "#F0F0F0",
  },
  button: {
    paddingHorizontal: 24,
    paddingVertical: 8,
    borderRadius: 100,
    minWidth: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "transparent",
  },
  cancelButtonText: {
    color: "#585858",
    fontSize: 14,
    fontWeight: "700",
  },
  saveButton: {
    backgroundColor: "#034895",
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "700",
  },
});

export default AddTertiaryModal;
